"use client";

import * as React from "react";
import Image from "next/image";
import { useChat, type Message } from "ai/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";
import {
  applyHireBuilderUpdates,
  buildTrishPostPreviewConfirmationMessage,
  buildTrishPostPreviewFollowUpMessage,
  extractHireBuilderConnectAction,
  extractHireBuilderPayload,
  extractTeamHandoffActionsFromAssistantText,
  stripHireBuilderStructuredBlock,
  type HireBuilderConnectAction,
  type HireBuilderMarketingBrand,
  type HireBuilderStructuredPayload,
  type HireBuilderTeamContact,
  type HireBuilderWidgetColors,
} from "~/lib/marketplace/hire-builder";
import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";
import { normalizeHireTeamHandoffCtaClusters } from "~/lib/marketplace/hire-team-handoff-ctas";
import type { HireOrganizationLayoutMode } from "~/lib/marketplace/hire-organization-layout";
import type { HireWizardToolCredentials } from "~/lib/marketplace/hire-wizard-draft";
import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  HIRE_BUILDER_CHAT_SESSION_KEY,
  HIRE_ONBOARDING_INITIAL_PROMPT_KEY,
  normalizeHireWebsiteSessionKey,
  readHireTrishOnboardingTranscript,
} from "~/lib/marketplace/hire-onboarding-session";
import { HireBuilderChatConnectPanel } from "~/components/marketplace/hire-builder-chat-connect-panel";

const TRISH_AVATAR_SRC = "/marketing/trish-onboarding-agent.png";
/** Bump when post-preview Trish copy / plan framing changes so stale sessions reset. */
const HIRE_BUILDER_CHAT_PROMPT_VERSION = "siteglue-onboard-v2";
/** One-shot flag so Google OAuth return auto-continues Trish across remounts. */
const HIRE_GOOGLE_CONNECT_RESUME_KEY = "egpt-hire-google-connect-resume";

export function markHireGoogleConnectResumePending(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HIRE_GOOGLE_CONNECT_RESUME_KEY, "1");
  } catch {
    /* ignore */
  }
}

function consumeHireGoogleConnectResumePending(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(HIRE_GOOGLE_CONNECT_RESUME_KEY) !== "1") return false;
    sessionStorage.removeItem(HIRE_GOOGLE_CONNECT_RESUME_KEY);
    return true;
  } catch {
    return false;
  }
}

type HireBuilderBuildChatProps = {
  businessName: string;
  websiteUrl: string;
  agentName: string;
  jobTitle: string;
  selection: HireRecommendationsSelection;
  widgetColors?: HireBuilderWidgetColors;
  organizationLayout?: HireOrganizationLayoutMode;
  teamContacts?: HireBuilderTeamContact[];
  previewReady?: boolean;
  disabled?: boolean;
  previewProjectId?: string | null;
  previewHireIntentId?: string | null;
  templateProjectId?: string | null;
  toolCredentials?: HireWizardToolCredentials;
  onToolCredentialsChange?: (next: HireWizardToolCredentials) => void;
  marketingBrand?: HireBuilderMarketingBrand | null;
  aiEmployeePlanId?: AiEmployeePlanId | null;
  /** After Google OAuth return — auto-send so Trish continues without typing "done". */
  resumeAfterGoogleConnect?: boolean;
  onResumeAfterGoogleConnectConsumed?: () => void;
  /** Persist confirmed agent identity across Google OAuth redirect. */
  agentIdentity?: {
    agentName?: string;
    jobTitle?: string;
    profileImageUrl?: string;
    agentNameConfirmed?: boolean;
  };
  /** When true, ignore LLM hire_builder agentName overrides (keeps Sunday/Grace). */
  lockAgentName?: boolean;
  onApplyUpdates: (updates: {
    companyName?: string;
    websiteUrl?: string;
    agentName?: string;
    jobTitle?: string;
    selection?: HireRecommendationsSelection;
    widgetColors?: HireBuilderWidgetColors;
  }) => void;
  className?: string;
};

type SavedHireBuilderChat = {
  websiteKey: string;
  messages: Message[];
  bootstrapped: boolean;
  promptVersion?: string;
};

function messageContentToString(content: Message["content"]): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === "string") return part;
        if (part && typeof part === "object" && "text" in part && typeof part.text === "string") {
          return part.text;
        }
        return "";
      })
      .join("");
  }
  return "";
}

function readSavedChat(websiteKey: string): SavedHireBuilderChat | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(HIRE_BUILDER_CHAT_SESSION_KEY) ||
      localStorage.getItem(HIRE_BUILDER_CHAT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedHireBuilderChat;
    if (parsed.websiteKey !== websiteKey) return null;
    if (parsed.promptVersion !== HIRE_BUILDER_CHAT_PROMPT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSavedChat(session: SavedHireBuilderChat) {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.stringify({
      ...session,
      promptVersion: HIRE_BUILDER_CHAT_PROMPT_VERSION,
    });
    sessionStorage.setItem(HIRE_BUILDER_CHAT_SESSION_KEY, raw);
    localStorage.setItem(HIRE_BUILDER_CHAT_SESSION_KEY, raw);
  } catch {
    /* ignore */
  }
}

function HireBuilderAssistantMessage({ content }: { content: string }) {
  const display = stripHireBuilderStructuredBlock(content);
  if (!display.trim()) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      className="prose prose-sm max-w-none text-foreground prose-headings:mb-2 prose-headings:mt-3 prose-headings:font-semibold prose-headings:text-foreground prose-li:my-0.5 prose-p:my-1.5 prose-p:leading-relaxed prose-strong:font-semibold [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
    >
      {display}
    </ReactMarkdown>
  );
}

export function HireBuilderBuildChat({
  businessName,
  websiteUrl,
  agentName,
  jobTitle,
  selection,
  widgetColors,
  organizationLayout,
  teamContacts,
  disabled,
  previewProjectId,
  previewHireIntentId,
  templateProjectId,
  toolCredentials = {},
  onToolCredentialsChange,
  marketingBrand = null,
  aiEmployeePlanId = null,
  resumeAfterGoogleConnect = false,
  onResumeAfterGoogleConnectConsumed,
  agentIdentity,
  lockAgentName = false,
  onApplyUpdates,
  className,
}: HireBuilderBuildChatProps) {
  const websiteKey = normalizeHireWebsiteSessionKey(websiteUrl || businessName);
  const initialPromptRef = React.useRef<string | null>(null);
  const bootstrappedRef = React.useRef(false);
  const appliedPayloadsRef = React.useRef<Set<string>>(new Set());
  const googleResumeSentRef = React.useRef(false);
  const [forcedConnectIntegration, setForcedConnectIntegration] = React.useState<
    HireBuilderConnectAction["integration"] | null
  >(null);

  if (initialPromptRef.current === null && typeof window !== "undefined") {
    initialPromptRef.current =
      sessionStorage.getItem(HIRE_ONBOARDING_INITIAL_PROMPT_KEY)?.trim() || null;
  }

  const savedSession = React.useMemo(
    () => (websiteKey ? readSavedChat(websiteKey) : null),
    [websiteKey],
  );

  const contextBody = React.useMemo(
    () => ({
      businessName,
      websiteUrl,
      agentName,
      jobTitle,
      selection,
      widgetColors,
      organizationLayout,
      teamContacts,
      initialPrompt: initialPromptRef.current,
      marketingBrand,
      aiEmployeePlanId,
    }),
    [
      businessName,
      websiteUrl,
      agentName,
      jobTitle,
      selection,
      widgetColors,
      organizationLayout,
      teamContacts,
      marketingBrand,
      aiEmployeePlanId,
    ],
  );

  const { messages, input, setInput, handleSubmit, append, isLoading, setMessages } = useChat({
    api: "/api/marketplace/hire-builder",
    body: { context: contextBody },
    initialMessages: savedSession?.messages ?? [],
  });

  React.useEffect(() => {
    if (savedSession?.bootstrapped) {
      bootstrappedRef.current = true;
    }
  }, [savedSession?.bootstrapped]);

  React.useEffect(() => {
    if (!websiteKey || messages.length === 0) return;
    writeSavedChat({
      websiteKey,
      messages,
      bootstrapped: bootstrappedRef.current,
    });
  }, [websiteKey, messages]);

  React.useEffect(() => {
    if (bootstrappedRef.current || disabled) return;
    if (savedSession?.messages?.length) {
      bootstrappedRef.current = true;
      return;
    }
    bootstrappedRef.current = true;

    // Carry forward pre-preview Trish transcript, then confirm what was generated
    // and start step-by-step onboarding (question 1) — no "Hi again" wipe.
    const prior = websiteKey ? readHireTrishOnboardingTranscript(websiteKey) : [];
    const confirmation: Message = {
      id: `trish-confirm-${Date.now()}`,
      role: "assistant",
      content: buildTrishPostPreviewConfirmationMessage({
        agentName,
        jobTitle,
        businessName,
        selection,
        organizationLayout,
        marketingBrand,
        aiEmployeePlanId,
      }),
    };
    setMessages([
      ...prior.map(
        (m): Message => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }),
      ),
      confirmation,
    ]);

    if (marketingBrand === "siteglue") {
      window.setTimeout(() => {
        setMessages((prev) => {
          if (prev.some((m) => m.id.startsWith("trish-followup-"))) return prev;
          return [
            ...prev,
            {
              id: `trish-followup-${Date.now()}`,
              role: "assistant",
              content: buildTrishPostPreviewFollowUpMessage(agentName),
            },
          ];
        });
      }, 15_000);
    }
  }, [
    agentName,
    businessName,
    disabled,
    jobTitle,
    organizationLayout,
    selection,
    setMessages,
    savedSession?.messages?.length,
    websiteKey,
    marketingBrand,
    aiEmployeePlanId,
  ]);

  const applyPayload = React.useCallback(
    (payload: HireBuilderStructuredPayload) => {
      const key = JSON.stringify(payload);
      if (appliedPayloadsRef.current.has(key)) return;
      appliedPayloadsRef.current.add(key);

      const safePayload =
        lockAgentName && payload.agentName
          ? { ...payload, agentName: undefined }
          : payload;

      const result = applyHireBuilderUpdates({
        payload: safePayload,
        companyName: businessName,
        websiteUrl,
        agentName,
        jobTitle,
        selection,
        widgetColors,
      });

      if (result.changed) {
        onApplyUpdates({
          companyName: result.companyName !== businessName ? result.companyName : undefined,
          websiteUrl: result.websiteUrl !== websiteUrl ? result.websiteUrl : undefined,
          agentName:
            !lockAgentName && result.agentName !== agentName ? result.agentName : undefined,
          jobTitle: result.jobTitle !== jobTitle ? result.jobTitle : undefined,
          selection: result.selection,
          widgetColors: result.widgetColors,
        });
      }
    },
    [
      agentName,
      businessName,
      jobTitle,
      lockAgentName,
      onApplyUpdates,
      selection,
      websiteUrl,
      widgetColors,
    ],
  );

  // After Google OAuth full-page return, auto-continue so Trish doesn't wait for "done".
  React.useEffect(() => {
    if (disabled || isLoading) return;
    if (googleResumeSentRef.current) return;
    if (!bootstrappedRef.current && messages.length === 0) return;
    const shouldResume =
      resumeAfterGoogleConnect || consumeHireGoogleConnectResumePending();
    if (!shouldResume) return;
    googleResumeSentRef.current = true;
    void append({
      role: "user",
      content: "Google Calendar is connected.",
    });
    onResumeAfterGoogleConnectConsumed?.();
  }, [
    append,
    disabled,
    isLoading,
    messages.length,
    onResumeAfterGoogleConnectConsumed,
    resumeAfterGoogleConnect,
  ]);

  React.useEffect(() => {
    for (const message of messages) {
      if (message.role !== "assistant") continue;
      const content = messageContentToString(message.content);
      const payload = extractHireBuilderPayload(content);
      if (payload) applyPayload(payload);
      else {
        const labels = extractTeamHandoffActionsFromAssistantText(content);
        if (labels?.length) {
          const fallbackClusters = labels.map((ctaQuestion) => ({
            ctaQuestion,
            qualificationQuestions: [
              "How can we help?",
              "What is the best phone number to reach you?",
            ],
            locationRouting: "all",
          }));
          const nextClusters = normalizeHireTeamHandoffCtaClusters(
            fallbackClusters,
            selection.teamHandoffCtaClusters ?? [],
          );
          const same =
            nextClusters.length === (selection.teamHandoffCtaClusters?.length ?? 0) &&
            nextClusters.every(
              (c, i) => c.ctaQuestion === selection.teamHandoffCtaClusters?.[i]?.ctaQuestion,
            );
          if (!same) {
            onApplyUpdates({
              selection: {
                ...selection,
                teamHandoffEnabled: true,
                teamHandoffCtaClusters: nextClusters,
              },
            });
          }
        }
      }
      const connectAction = extractHireBuilderConnectAction(content);
      if (connectAction?.integration) {
        setForcedConnectIntegration(connectAction.integration);
      }
    }
  }, [messages, applyPayload, onApplyUpdates, selection]);

  // Also show connect panel when selection already has a scheduling/EMR tool.
  React.useEffect(() => {
    if (forcedConnectIntegration) return;
    if (
      selection.appointmentsEnabled &&
      (selection.schedulingIntegrationId === "google_calendar" ||
        selection.schedulingIntegrationId === "calendly" ||
        selection.schedulingIntegrationId === "zoom")
    ) {
      setForcedConnectIntegration(selection.schedulingIntegrationId);
      return;
    }
    if (
      selection.emrIntegrationEnabled &&
      selection.emrIntegrationId !== "none"
    ) {
      setForcedConnectIntegration(
        selection.emrIntegrationId as HireBuilderConnectAction["integration"],
      );
    }
  }, [
    forcedConnectIntegration,
    selection.appointmentsEnabled,
    selection.schedulingIntegrationId,
    selection.emrIntegrationEnabled,
    selection.emrIntegrationId,
  ]);

  const showConnectPanel =
    Boolean(forcedConnectIntegration) ||
    (selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none") ||
    (selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none");

  const visibleMessages = messages.filter((m) => {
    if (m.role === "assistant") {
      return stripHireBuilderStructuredBlock(messageContentToString(m.content)).trim().length > 0;
    }
    return true;
  });

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollToBottom = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  return (
    <div className={cn("flex min-h-[520px] flex-col", className)}>
      <div className="mb-4 flex shrink-0 items-start gap-3">
        <Image
          src={TRISH_AVATAR_SRC}
          alt="Trish"
          width={40}
          height={40}
          className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white"
        />
        <div>
          <h3 className="text-base font-semibold text-slate-900">Trish</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {marketingBrand === "siteglue"
              ? "I'll confirm your SiteGlue plan and channels, then walk through scheduling, locations, team, colors, and handoff — all in this chat."
              : "I'll list your settings here and walk through channels, scheduling, locations, team, colors, and handoff actions — you can finish everything in this chat."}
          </p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-4"
      >
        {visibleMessages.length === 0 && isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Image
              src={TRISH_AVATAR_SRC}
              alt=""
              width={32}
              height={32}
              className="h-8 w-8 rounded-full object-cover opacity-80"
            />
            <Loader2 className="h-4 w-4 animate-spin" />
            Trish is getting ready…
          </div>
        ) : null}

        {visibleMessages.map((message) =>
          message.role === "user" ? (
            <div
              key={message.id}
              className="ml-auto max-w-[92%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
            >
              <p className="whitespace-pre-wrap">{messageContentToString(message.content)}</p>
            </div>
          ) : (
            <div key={message.id} className="flex max-w-[92%] gap-2">
              <Image
                src={TRISH_AVATAR_SRC}
                alt="Trish"
                width={32}
                height={32}
                className="mt-0.5 h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white"
              />
              <div className="rounded-lg bg-white px-3 py-2 text-sm text-slate-800 shadow-sm ring-1 ring-slate-200">
                <HireBuilderAssistantMessage content={messageContentToString(message.content)} />
              </div>
            </div>
          ),
        )}

        {isLoading && visibleMessages.length > 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Image
              src={TRISH_AVATAR_SRC}
              alt=""
              width={32}
              height={32}
              className="h-8 w-8 rounded-full object-cover opacity-80"
            />
            <Loader2 className="h-4 w-4 animate-spin" />
            Thinking…
          </div>
        ) : null}

        {showConnectPanel && onToolCredentialsChange && !isLoading ? (
          <HireBuilderChatConnectPanel
            selection={selection}
            onSelectionChange={(next) => onApplyUpdates({ selection: next })}
            credentials={toolCredentials}
            onCredentialsChange={onToolCredentialsChange}
            previewProjectId={previewProjectId}
            previewHireIntentId={previewHireIntentId}
            templateProjectId={templateProjectId}
            websiteUrl={websiteUrl}
            forcedIntegration={forcedConnectIntegration}
            siteGluePlanMode={marketingBrand === "siteglue"}
            agentIdentity={agentIdentity}
          />
        ) : null}
      </div>

      <form
        className="mt-4 flex shrink-0 gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!input.trim() || isLoading || disabled) return;
          void handleSubmit(event);
        }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Reply to Trish…"
          disabled={isLoading || disabled}
          className="flex-1"
        />
        <Button type="submit" disabled={isLoading || disabled || !input.trim()}>
          Send
        </Button>
      </form>

      {disabled ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Sign in and finish loading your website analysis to continue with Trish.
        </p>
      ) : null}
    </div>
  );
}
